import { useEffect, useState } from "react";
import { fetchGbpToCurrencyRate } from "../../utils/exchangeRates";

const CURRENCIES = ["GBP", "USD", "EUR"];

/**
 * Lets the user pick a target currency and see (or override) the GBP ->
 * target conversion rate. The extra RRP column in OrdersTable converts
 * every row's already-computed GBP figure into this one currency using
 * this one rate — it doesn't look up a rate per order's own checkout
 * currency. The rate field is always editable, not locked to whatever
 * was fetched, since some vendors' own company sends a fixed quarterly
 * rate rather than the live market rate.
 */
export default function CurrencyRates({ currency, rate, onCurrencyChange, onRateChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (currency === "GBP") {
      onRateChange(1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchGbpToCurrencyRate(currency)
      .then((liveRate) => {
        if (!cancelled) onRateChange(liveRate);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Couldn't fetch live rate — enter manually.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-fetch only when the target currency changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  function handleRateChange(value) {
    const parsed = parseFloat(value);
    onRateChange(value === "" || isNaN(parsed) ? null : parsed);
  }

  return (
    <div className="flex flex-wrap items-end gap-6 border border-solid border-collectors-black px-4 py-4">
      <div className="flex flex-col gap-1">
        <label className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
          Show RRP in
        </label>
        <select
          value={currency}
          onChange={(e) => onCurrencyChange(e.target.value)}
          className="bg-transparent border-solid border-b-2 border-collectors-light py-1 font-mono
                  text-sm text-collectors-black focus:outline-none focus:border-collectors-black"
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {currency !== "GBP" && (
        <div className="flex flex-col gap-1">
          <label className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
            1 GBP = {currency}
          </label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={rate ?? ""}
            onChange={(e) => handleRateChange(e.target.value)}
            placeholder={loading ? "Fetching..." : "rate"}
            className="w-24 bg-transparent border-solid border-b-2 border-collectors-light py-1 font-mono
                    text-sm text-collectors-black focus:outline-none focus:border-collectors-black"
          />
        </div>
      )}

      {error && (
        <span className="font-mono text-xs uppercase tracking-widest text-red-600">{error}</span>
      )}
    </div>
  );
}